import React from "react";

interface InitialAvatarProps {
  name: string;
  size?: number;
}

const InitialAvatar: React.FC<InitialAvatarProps> = ({ name, size = 40 }) => {
  // Function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "?";

    // Split by spaces
    const nameParts = name.split(" ").filter(part => part.length > 0);

    if (nameParts.length === 0) return "?";
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

    // Get first letter of first name and first letter of last name
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  };

  // Generate a consistent color pair based on the name
  const getColorPair = (name: string): { bg: string; text: string } => {
    const hash = name.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);

    // Soft pastel backgrounds with matching muted text colors
    const palette = [
      { bg: "#EDE9FE", text: "#6D28D9" }, // Lavender / violet
      { bg: "#DBEAFE", text: "#2563EB" }, // Soft blue
      { bg: "#D1FAE5", text: "#059669" }, // Sage green
      { bg: "#FEE2E2", text: "#DC2626" }, // Blush pink
      { bg: "#FEF3C7", text: "#D97706" }, // Warm cream / amber
      { bg: "#E0E7FF", text: "#4338CA" }, // Periwinkle / indigo
      { bg: "#FCE7F3", text: "#DB2777" }, // Soft rose
      { bg: "#CCFBF1", text: "#0D9488" }, // Mint teal
      { bg: "#FFEDD5", text: "#EA580C" }, // Peach / orange
      { bg: "#F3E8FF", text: "#7C3AED" }, // Light purple
      { bg: "#E0F2FE", text: "#0284C7" }, // Sky blue
      { bg: "#FEF9C3", text: "#CA8A04" }, // Soft yellow
    ];

    const index = Math.abs(hash) % palette.length;
    return palette[index];
  };

  const initials = getInitials(name);
  const { bg, text: textColor } = getColorPair(name);
  const fontSize = size * 0.4;

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: bg,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: textColor,
        fontWeight: 600,
        fontSize: `${fontSize}px`,
        fontFamily: "Inter, system-ui, sans-serif",
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </div>
  );
};

export default InitialAvatar;